/*
 * pin-product.js — Pin a single product to the top of a collection grid via ?pin=<handle>.
 *
 * Shopify Liquid can't read arbitrary query params server-side, so pinning is done here on
 * the client:
 *   - Reads the `pin` query param (a product handle).
 *   - If that product's card is already rendered in #product-grid, it's moved to the front.
 *   - Otherwise its card is fetched via the alternate product template
 *     (/products/<handle>?view=pin-card — see templates/product.pin-card.liquid) and
 *     prepended, so it works even when the product would land on page 2+ of the collection.
 *   - A MutationObserver removes the natural (non-pinned) duplicate if it later loads in
 *     via "load more" / infinite scroll.
 *
 * The grid is hidden (visibility) until pinning resolves — see the FOUC guard in
 * sections/main-collection-product-grid.liquid — so there is no visible reshuffle.
 */
(function () {
  'use strict';

  var handle;
  try {
    handle = new URLSearchParams(window.location.search).get('pin');
  } catch (e) {
    return;
  }
  if (!handle) return;
  handle = handle.trim().toLowerCase();

  function reveal() {
    document.documentElement.classList.remove('pin-pending');
  }

  if (!handle) {
    reveal();
    return;
  }

  // Safety net: never leave the grid hidden, even if a fetch hangs or something throws.
  var safety = window.setTimeout(reveal, 2500);
  function done() {
    window.clearTimeout(safety);
    reveal();
  }

  function esc(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/["\\\]]/g, '\\$&');
  }
  var handleSelector = '[data-product-handle="' + esc(handle) + '"]';

  function findCardLi(grid) {
    var card = grid.querySelector(handleSelector);
    return card ? card.closest('li.grid__item') : null;
  }

  // Drop any non-pinned copy of the pinned product that arrives later (pagination/load-more).
  function startDedupObserver(grid) {
    if (!window.MutationObserver) return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          var items = node.matches && node.matches('li.grid__item')
            ? [node]
            : (node.querySelectorAll ? node.querySelectorAll('li.grid__item') : []);
          for (var k = 0; k < items.length; k++) {
            var li = items[k];
            if (li.hasAttribute('data-pinned')) continue;
            if (li.querySelector(handleSelector)) li.remove();
          }
        }
      }
    });
    observer.observe(grid, { childList: true, subtree: true });
  }

  function fetchAndInject(grid) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var url = root.replace(/\/+$/, '') + '/products/' + encodeURIComponent(handle) + '?view=pin-card';
    fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then(function (html) {
        // The natural card may have raced in (load-more) while we were fetching.
        if (findCardLi(grid)) {
          done();
          return;
        }
        var template = document.createElement('template');
        template.innerHTML = html.trim();
        var li = template.content.querySelector('li.grid__item');
        if (li && li.querySelector(handleSelector)) {
          li.setAttribute('data-pinned', 'true');
          grid.insertBefore(li, grid.firstElementChild);
        }
        done();
      })
      .catch(function () {
        // Best-effort: an invalid handle (404) or network error just leaves the grid as-is.
        done();
      });
  }

  function run() {
    var grid = document.getElementById('product-grid');
    // The empty-collection state also uses id="product-grid" but is a <div>; only pin into the <ul>.
    if (!grid || grid.tagName !== 'UL') {
      done();
      return;
    }
    startDedupObserver(grid);

    var existing = findCardLi(grid);
    if (existing) {
      existing.setAttribute('data-pinned', 'true');
      if (grid.firstElementChild !== existing) grid.insertBefore(existing, grid.firstElementChild);
      done();
      return;
    }
    fetchAndInject(grid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
